import { Controller, useForm } from 'react-hook-form';
import { Trans } from '@lingui/react';
import { Sheet } from '@startuptools/ui/joy/Sheet';
import { Typography } from '@startuptools/ui/joy/Typography';
import { Button } from '@startuptools/ui/joy/Button';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { Stack } from '@startuptools/ui/joy/Stack';
import { Alert } from '@startuptools/ui/joy/Alert';
import { OnReject } from './types';
import { Radio } from '@startuptools/ui/joy/Radio';
import { RadioGroup } from '@startuptools/ui/joy/RadioGroup';
import { isNil } from 'lodash-es';
import { FormControl } from '@startuptools/ui/FormControl';

interface FormData {
  governmentOwnership: boolean;
}

export const GovernmentOwnership = ({ onReject }: { onReject: OnReject }) => {
  const { stepNext } = useStepper();

  const { handleSubmit, control, watch } = useForm<FormData>();

  const onFormSubmit = handleSubmit(async ({ governmentOwnership }) => {
    if (!governmentOwnership) {
      onReject({
        title: <Trans id="The government owns too large a share of the company" />,
        description: <Trans id="the government owns 25% or more of the company" />,
      });
    } else {
      await stepNext();
    }
  });

  const formValues = watch();

  return (
    <form onSubmit={onFormSubmit}>
      <Sheet
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          width: '100%',
        }}
      >
        <Typography level="h4">
          <Trans id="Does the government own 25% or more of the company?" />
        </Typography>

        <Alert variant="outlined" color="primary">
          <Stack>
            <Typography level="body-sm">
              <Trans id="To issue qualified employee stock options, the government cannot own 25% or more of the company. This includes indirect ownership, for example through investment companies like Almi Invest AB." />
            </Typography>
          </Stack>
        </Alert>

        <Sheet>
          <Controller
            control={control}
            name="governmentOwnership"
            render={({ field: { value, onChange, ...field } }) => (
              <FormControl>
                <RadioGroup {...field} value={value} onChange={e => onChange(e.target.value === 'true')}>
                  <Radio
                    value="true"
                    label={<Trans id="The government owns, directly or indirectly, nothing or less than 25%" />}
                  />
                  <Radio
                    value="false"
                    label={<Trans id="The government owns, directly or indirectly, 25% or more of the company" />}
                  />
                </RadioGroup>
              </FormControl>
            )}
          />
        </Sheet>

        <Sheet sx={{ pt: 2 }}>
          <Button type="submit" disabled={isNil(formValues.governmentOwnership)}>
            <Trans id="Continue" />
          </Button>
        </Sheet>
      </Sheet>
    </form>
  );
};
